import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Button,
  Grid,
  TextField,
  InputAdornment
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Navbar from "../../components/Navbar";
import { useNavigate } from "react-router-dom";
import { useDocs } from "../../hooks/useDocs";
import ProgressLoader from "../../components/Common/ProgressLoader";
import SearchIcon from "@mui/icons-material/Search";
import ConfirmationModal from "../../components/Common/ConfirmationModal";

export default function BubblesManagement() {
  const [bubbles, setBubbles] = useState([]);
  const [loader, setLoader] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedBubble, setSelectedBubble] = useState(null);
  const [bubbleToDelete, setBubbleToDelete] = useState(null);

  const navigate = useNavigate();
  const { getAllBubbles, deleteBubble } = useDocs();

  // Load JSON
  useEffect(() => {
    getBubbles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBubbles = async () => {
    const data = await getAllBubbles();
    setBubbles(data);
    setLoader(false);
  };

  const handleEdit = (bubble) => {
    navigate(`/change-bubbles/edit/${bubble.id}`, {
      state: { editableBubble: bubble },
    });
    setSelectedBubble(bubble);
  };

  const handleConfirmDelete = async () => {
    if (!bubbleToDelete) return;

    // Optimistic update
    setBubbles((prev) => prev.filter((b) => b.id !== bubbleToDelete.id));

    try {
      await deleteBubble(bubbleToDelete.id, bubbleToDelete.experienceId);
    } catch (error) {
      console.error("Delete failed, refreshing data", error);
      getBubbles(); // fallback to sync again if API fails
    } finally {
      setOpenConfirm(false);
      setBubbleToDelete(null);
    }
  };

  const handleDeleteClick = (bubble) => {
    setBubbleToDelete(bubble);
    setOpenConfirm(true);
  };

  const handleAdd = () => {
    navigate("/change-bubbles/create");
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 1 },
    { field: "title", headerName: "Title", flex: 1 },
    { field: "experienceId", headerName: "Type", flex: 1 },
    {
      field: "actions",
      headerName: "Action",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            color="primary"
            onClick={() => handleEdit(params.row)}
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDeleteClick(params.row)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleCancelDelete = () => {
    setOpenConfirm(false);
    setBubbleToDelete(null);
  };

  const filteredRows = bubbles.filter((row) => {
    if (!searchTerm) return true;
    const lower = searchTerm.toLowerCase();
    return (
      row.id?.toString().toLowerCase().includes(lower) ||
      row.title?.toLowerCase().includes(lower) ||
      row.experienceId?.toLowerCase().includes(lower)
    );
  });

  return (
    <Box className="overflow-hidden h-screen">
      <Navbar />
      <Box className="px-10">
        <Box className="flex justify-between my-5">
          <Grid item xs={4}>
            <TextField
              size="small"
              label="Quick Search"
              sx={{ bgcolor: "background.paper" }}
              value={searchTerm}
              className="!mr-1 max-w-full md:max-w-full"
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            CREATE NEW
          </Button>
        </Box>

        <Grid className="assets-cards" container>
          {loader ? (
            <ProgressLoader />
          ) : (
            <DataGrid
              rows={filteredRows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              }}
              value={selectedBubble}
              autoPageSize={false}
              disableAutosize={false}
              pageSizeOptions={[5, 10, 25, 40, 100]}
              checkboxSelection={false}
              className="mb-3"
              sx={{
                bgcolor: "background.paper",
                position: "relative",
              }}
            />
          )}
        </Grid>

        {/* Confirmation Dialog */}
        <ConfirmationModal 
            isOpened={openConfirm}
            handleCancle={handleCancelDelete}
            handleConfirmation={handleConfirmDelete}
            title={"Confirm Deletion"}
            description={"Are you sure you want to delete ?"}
            buttonTitle={"Delete"}
        />
      </Box>
    </Box>
  );
}
